import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Added Framer Motion imports
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo, getLocation } from '../utils/deviceInfo';
const API_URL = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [form, setForm] = useState({
    applicationType: '',
    username: '',
    password: '',
    otp: '',
  });

  const [errors, setErrors] = useState({
    login: '',
    otp: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingOTP, setLoadingOTP] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const navigate = useNavigate();
  const [loginResponse, setLoginResponse] = useState([]);
  const [access_data, setAccess_data] = useState([]);
  const applicationOptions = [
    { name: 'Admin', value: 1 },
    { name: 'Employee', value: 2 },
    { name: 'Shareholder', value: 3 },
  ];

  const handleSelect = (roleName) => {
    setForm({ ...form, applicationType: roleName });
  };

  useEffect(() => {
    if (sessionCheck() != null) {
      navigate("../dashboard", { replace: true });
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (e.target.name === 'otp') {
      setErrors({ ...errors, otp: '' });
    }
  };

  const handleLocationError = (error) => {
    console.error("Location error:", error);
    let errorMessage = 'Unable to determine your location.';

    if (error?.code === 1 || error?.message?.includes("permission denied")) {
      errorMessage = 'Location permission denied. Please enable location access in settings.';
    } else if (error?.code === 2 || error?.message?.includes("unavailable")) {
      errorMessage = 'Location services unavailable. Please check your connection.';
    } else if (error?.code === 3 || error?.message?.includes("timeout")) {
      errorMessage = 'Location request timed out. Please try again.';
    } else if (error?.code === 0 || error?.message?.includes("unknown")) {
      errorMessage = 'Location could not be determined. Please try again in an area with better signal.';
    }

    setLocationError(errorMessage);
    return false;
  };

  const validateForm = () => {
    const errors = {};

    if (!form.applicationType) {
      errors.applicationType = 'Please select application type';
    }

    if (!form.username.trim()) {
      errors.username = 'Enter username';
    }

    if (!form.password) {
      errors.password = 'Enter password';
    } else if (form.password.length < 10) {
      errors.password = 'Minimum password length is 10';
    } else if (form.password.length > 16) {
      errors.password = 'Maximum password length is 16';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return false;
    }

    setFormErrors({});
    return true;
  };

  const validateOTP = () => {
    if (!form.otp.trim()) {
      setErrors({ ...errors, otp: 'Please enter OTP' });
      return false;
    } else if (form.otp.length < 6) {
      setErrors({ ...errors, otp: 'OTP must be 6 digits' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocationError(null);
    setErrors({ ...errors, login: '' });

    if (!validateForm()) return;

    setLoading(true);

    try {

      const deviceInfo = getDeviceInfo();
      let coords = {
        latitude: null,
        longitude: null
      };

      try {
        coords = await getLocation();
        if (!coords.latitude || !coords.longitude) {
          throw new Error('Location coordinates not available');
        }
      } catch (error) {
        handleLocationError(error);
        setLoading(false);
        return;
      }

      const selectedRole = applicationOptions.find(
        (role) => role.name === form.applicationType
      );

      if (!selectedRole) {
        setErrors({ ...errors, login: 'Invalid role selected' });
        setLoading(false);
        return;
      }

      const dataToEncrypt = {
        payload: {
          applicationType: selectedRole.value,
          username: form.username,
          password: form.password,
          latitude: coords.latitude,
          longitude: coords.longitude,
          deviceId: getDeviceInfo().deviceId,
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        setErrors({ ...errors, login: 'Encryption failed. Please try again.' });
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}login.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        if (result.data.login_status) {
          setLoginResponse(result);
          setShowOTPModal(true);
        } else {
          setErrors({ ...errors, login: 'Login failed. Please check your credentials.' });
        }
      } else {
        setErrors({ ...errors, login: result.message || "Login failed. Please check your credentials." });
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ ...errors, login: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLocationError(null);

    if (!validateOTP()) return;

    setLoadingOTP(true);

    try {

      const deviceInfo = getDeviceInfo();
      let coords = {
        latitude: null,
        longitude: null
      };

      try {
        coords = await getLocation();
        if (!coords.latitude || !coords.longitude) {
          throw new Error('Location coordinates not available');
        }
      } catch (error) {
        handleLocationError(error);
        setLoadingOTP(false);
        return;
      }


      const selectedRole = applicationOptions.find(
        (role) => role.name === form.applicationType
      );

      if (!selectedRole) {
        setErrors({ ...errors, otp: 'Invalid role selected' });
        setLoadingOTP(false);
        return;
      }

      const dataToEncrypt = {
        payload: {
          applicationType: selectedRole.value,
          username: form.username,
          password: form.password,
          latitude: coords.latitude,
          longitude: coords.longitude,
          deviceId: deviceInfo.deviceId,
          login_id: loginResponse.data.login_id,
          otp: form.otp,
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        setErrors({ ...errors, otp: 'Encryption failed. Please try again.' });
        setLoadingOTP(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}verify.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true && result.data.login_verification_status) {
        const loginData = {
          username: result.data.username,
          login_id: result.data.login_id,
        };
        const userData = {
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
          avatar: result.user.avatar,
        };
        localStorage.setItem("user_data", JSON.stringify(userData));
        localStorage.setItem("access_data", JSON.stringify(loginData));
        navigate("../dashboard", { replace: true });
      } else {
        setErrors({ ...errors, otp: result.message || "OTP verification failed. Please try again." });
      }
    } catch (error) {
      console.error("OTP error:", error);
      setErrors({ ...errors, otp: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoadingOTP(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="flex flex-col md:flex-row bg-white rounded-2xl shadow-lg overflow-hidden w-full max-w-4xl">
        {/* Left Panel */}
        <div className="hidden md:flex md:w-1/2 bg-blue-600 items-center justify-center p-10 text-white">
          <h2 className="text-4xl font-bold">Welcome to Our Platform</h2>
        </div>

        {/* Right Panel */}
        <div className="flex w-full md:w-1/2 items-center justify-center p-8">
          <form className="w-full max-w-sm space-y-6" onSubmit={handleSubmit}>
            <h2 className="text-2xl font-bold mb-4 text-center">Login</h2>

            <AnimatePresence>
              {errors.login && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="p-3 bg-red-50 text-red-600 rounded-md text-sm"
                >
                  {errors.login}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Application Type */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Application Type</label>
              <Menu as="div" className="relative">
                <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-left text-sm focus:outline-none">
                  <span>{form.applicationType || 'Select Application'}</span>
                  <ChevronDownIcon className="h-4 w-4" />
                </Menu.Button>
                <Menu.Items className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {applicationOptions.map((role) => (
                    <Menu.Item key={role.name}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => handleSelect(role.name)}
                          className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          {role.name}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
              {formErrors.applicationType && (
                <p className="text-red-500 text-xs mt-1">{formErrors.applicationType}</p>
              )}
            </div>

            {/* Username */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                name="username"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={form.username}
                onChange={handleChange}
                required
              />
              {formErrors.username && (
                <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                name="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={form.password}
                onChange={handleChange}
                required
              />
              {formErrors.password && (
                <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
              )}
            </div>

            {locationError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm p-2 bg-red-50 rounded-md"
              >
                {locationError}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white py-2 rounded-md transition ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>

      {/* OTP Modal */}
      <Transition appear show={showOTPModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowOTPModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-center px-6 pt-6 pb-2 border-b">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      OTP Verification
                    </Dialog.Title>
                    <button onClick={() => setShowOTPModal(false)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="px-6 py-4">
                    <AnimatePresence>
                      {errors.otp && (
                        <motion.div
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                          className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm"
                        >
                          {errors.otp}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Enter OTP</label>
                      <input
                        type="text"
                        name="otp"
                        value={form.otp}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter OTP"
                      />
                    </div>
                  </div>

                  {locationError && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="px-6 pb-2 text-red-500 text-sm"
                    >
                      {locationError}
                    </motion.div>
                  )}

                  <div className="px-6 py-4 border-t flex justify-between items-center">
                    <button className="text-blue-600 hover:underline text-sm">Resend OTP</button>

                    <button
                      onClick={handleOTPSubmit}
                      disabled={loadingOTP}
                      className={`px-3 text-white py-2 rounded-md transition ${loadingOTP ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                      {loadingOTP ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default LoginPage;